import {
  DEFAULT_ONBOARDING_DRAFT,
  type OnboardingDraft,
  type OnboardingOwnerUserId,
  withOnboardingDraftOwner,
} from './draft'
import {
  type OnboardingClassStyle,
  type OnboardingBookingLanguage,
  type OnboardingEntrySource,
  type OnboardingGoal,
  type OnboardingLanguage,
  type OnboardingPreferredTime,
} from './schema'

export type OnboardingHomeState = {
  entrySource: string | null
  language: 'EN' | 'JP' | 'KR' | null
  goals: string[]
  experienceLevel: number | null
  frequency: number | null
  preferredTimes: string[]
  classStyles: string[]
  recommendedLevel: string | null
  selectedLevel: string | null
  styleTags: string[]
  lessonStyleTags: string[]
  selectedClassId: number | null
  prestudyClassId?: number | null
  selectedClassDatetime?: string | null
  trialBooked: boolean
  trialSkipped: boolean
  marketingAgreed: boolean
  notificationResponded: boolean
  completed: boolean
}

export type TrialExperienceHomeCardType =
  | 'NOT_RESERVED'
  | 'RESERVED_WAITING'
  | 'FIRST_NOSHOW'
  | 'COMPLETED'
  | 'DOUBLE_NOSHOW'
  | 'UNKNOWN'

export type TrialExperienceHomeCardCta = {
  label: string
  action: string
  target: string | null
  url: string | null
  enabled: boolean
}

export type TrialExperienceHomeCardReservation = {
  classId: number | null
  prestudyClassId: number | null
  classStartAt: string | null
  classEndAt: string | null
  dDay: number | null
  lessonName: string | null
  tutorId: number | null
  tutorName: string | null
}

export type TrialExperienceHomeCardLessonPreview = {
  classCourseId: number | null
  title: string | null
  classLevel: number | null
  classWeek: number | null
  curriculumType: string | null
  lessonTime: number | null
  thumbnailUrl: string | null
  description: string | null
}

export type TrialExperienceHomeCardDiscountBadge = {
  visible: boolean
  label: string | null
  source: string | null
  fallback: boolean
}

export type TrialExperienceHomeCardPreparationItem = {
  type: string
  label: string
  action: string
  url: string | null
  enabled: boolean
}

export type TrialExperienceHomeCardFallback = {
  enabled: boolean
  reason: string | null
  message: string | null
}

export type TrialExperienceHomeCard = {
  cardType: TrialExperienceHomeCardType
  title: string
  description: string | null
  primaryCta: TrialExperienceHomeCardCta | null
  secondaryCta: TrialExperienceHomeCardCta | null
  reservation: TrialExperienceHomeCardReservation | null
  lessonPreview: TrialExperienceHomeCardLessonPreview | null
  discountBadge: TrialExperienceHomeCardDiscountBadge | null
  preparationItems: TrialExperienceHomeCardPreparationItem[]
  fallback: TrialExperienceHomeCardFallback | null
}

export type TrialExperienceHomeState = {
  state: TrialExperienceHomeCardType
  reason: string | null
  language: 'EN' | 'JP' | 'KR' | null
  classId: number | null
  prestudyClassId: number | null
  classTicketId: string | null
  classCourseId: number | null
  trialLevel: string | null
  classStartAt: string | null
  noShowCount: number
  availableActions: string[]
  hasUnbookedTrialTicket?: boolean
  paidTrial?: boolean | null
  bookingClassId?: number | null
  homeCard: TrialExperienceHomeCard | null
}

export type LanguageTrialType = 'EN' | 'JP'

type BookedOnboardingStateLike = Pick<OnboardingHomeState, 'trialBooked' | 'selectedClassId'>
type ResumableAdditionalTrialStateLike = Pick<
  OnboardingHomeState,
  'entrySource' | 'language' | 'selectedLevel' | 'trialBooked'
>
type PurchaseableTrialTicketLike = {
  lang_type?: string | null
  langType?: string | null
}

type HomeCardUserStatus = 'Y' | 'N' | string

type HomeBookedCardParams = {
  draft: OnboardingDraft
  userPaymentYn: HomeCardUserStatus
  userTrialPaymentYn: HomeCardUserStatus
  userTrialClassCompYn: HomeCardUserStatus
  hasUpcomingLesson: boolean
  upcomingClassId?: number | null
  hasBookedOnboardingState?: boolean
  hasRegularLessonEntitlement?: boolean
  hasSkippedAdditionalTrialOnboardingState?: boolean
}

const ONBOARDING_ENTRY_SOURCES = ['intro', 'mypage_reentry', 'legacy_trial', 'admin_force'] as const
const LANGUAGE_TRIAL_TYPES = ['EN', 'JP'] as const
const BUNDLE_LANGUAGE_TYPE = 'ENJP'

const parseLanguageInfo = (value: string | null | undefined) =>
  new Set(
    (value ?? '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
  )

const isLanguageTrialType = (value: string | null | undefined): value is LanguageTrialType =>
  LANGUAGE_TRIAL_TYPES.includes(value as LanguageTrialType)

const normalizeTrialLanguage = (language: OnboardingHomeState['language'] | OnboardingDraft['language']) => {
  if (language === 'en') {
    return 'EN'
  }

  if (language === 'ja') {
    return 'JP'
  }

  return isLanguageTrialType(language) ? language : null
}

const normalizeEntrySource = (entrySource: string | null): OnboardingEntrySource => {
  if (ONBOARDING_ENTRY_SOURCES.includes(entrySource as OnboardingEntrySource)) {
    return entrySource as OnboardingEntrySource
  }

  return DEFAULT_ONBOARDING_DRAFT.entrySource
}

const normalizeLanguage = (language: OnboardingHomeState['language']): OnboardingLanguage | null => {
  if (language === 'EN') {
    return 'en'
  }

  if (language === 'JP') {
    return 'ja'
  }

  return null
}

const normalizeBookingLanguage = (language: OnboardingHomeState['language']): OnboardingBookingLanguage | null => {
  if (language === 'EN') {
    return 'en'
  }
  if (language === 'JP') {
    return 'ja'
  }
  if (language === 'KR') {
    return 'ko'
  }
  return null
}

export const getHasBookedOnboardingState = (state: BookedOnboardingStateLike | null | undefined) => {
  return Boolean(state?.trialBooked && state.selectedClassId)
}

export const shouldShowTrialExperienceHomeCard = (state: TrialExperienceHomeState | null | undefined) => {
  const card = state?.homeCard
  if (!card || card.fallback?.enabled) {
    return false
  }

  return (
    card.cardType === 'NOT_RESERVED' ||
    card.cardType === 'FIRST_NOSHOW' ||
    card.cardType === 'COMPLETED' ||
    card.cardType === 'DOUBLE_NOSHOW'
  )
}

export const shouldShowReservedTrialLessonsSection = (state: TrialExperienceHomeState | null | undefined) => {
  const card = state?.homeCard
  return Boolean(
    card &&
    !card.fallback?.enabled &&
    card.cardType === 'RESERVED_WAITING' &&
    state?.language &&
    state.language !== 'KR',
  )
}

export const getPrimaryTrialExperienceHomeState = (
  states: readonly (TrialExperienceHomeState | null | undefined)[],
): TrialExperienceHomeState | null => {
  const candidates = states.filter((state): state is TrialExperienceHomeState => state !== null && state !== undefined)

  return (
    candidates.find((state) => {
      const card = state.homeCard
      return card?.cardType === 'RESERVED_WAITING' && !card.fallback?.enabled
    }) ??
    candidates[0] ??
    null
  )
}

export const shouldShowFirstTrialBookingToast = ({
  draft,
  state,
}: {
  draft: OnboardingDraft | null | undefined
  state: TrialExperienceHomeState | null | undefined
}) => {
  const card = state?.homeCard
  if (!card || card.fallback?.enabled || card.cardType !== 'RESERVED_WAITING') {
    return false
  }

  if (!draft) {
    return false
  }

  return draft.entrySource !== 'legacy_trial' && draft.isReusedFlow !== true
}

const getSelectedClassTimestamp = (state: OnboardingHomeState) => {
  if (!state.selectedClassDatetime) {
    return Number.POSITIVE_INFINITY
  }

  const timestamp = Date.parse(state.selectedClassDatetime)
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY
}

export const getBookedOnboardingState = (
  states: readonly (OnboardingHomeState | null | undefined)[],
  options: { selectedClassId?: number | null } = {},
) => {
  const selectedClassId = options.selectedClassId ? Number(options.selectedClassId) : null
  const bookedStates = states.filter((state): state is OnboardingHomeState =>
    Boolean(state && getHasBookedOnboardingState(state)),
  )

  if (selectedClassId) {
    return bookedStates.find((state) => Number(state.selectedClassId) === selectedClassId) ?? null
  }

  let earliestState: OnboardingHomeState | null = null
  let earliestTimestamp = Number.POSITIVE_INFINITY

  for (const state of bookedStates) {
    const timestamp = getSelectedClassTimestamp(state)
    if (!earliestState || timestamp < earliestTimestamp) {
      earliestState = state
      earliestTimestamp = timestamp
    }
  }

  return earliestState
}

export const getPurchaseableTrialLanguageTypes = (
  tickets: readonly PurchaseableTrialTicketLike[] | null | undefined,
): LanguageTrialType[] => {
  const languages = new Set<LanguageTrialType>()

  for (const ticket of tickets ?? []) {
    const language = ticket.lang_type ?? ticket.langType
    if (isLanguageTrialType(language)) {
      languages.add(language)
    }
  }

  return LANGUAGE_TRIAL_TYPES.filter((language) => languages.has(language))
}

export const getBookedLanguageTrialTypes = (
  states: readonly (Pick<OnboardingHomeState, 'language' | 'selectedClassId' | 'trialBooked'> | null | undefined)[],
): LanguageTrialType[] => {
  const languages = new Set<LanguageTrialType>()

  for (const state of states) {
    if (state?.trialBooked && state.selectedClassId && isLanguageTrialType(state.language)) {
      languages.add(state.language)
    }
  }

  return LANGUAGE_TRIAL_TYPES.filter((language) => languages.has(language))
}

export const getResumableAdditionalTrialLanguageTypes = (
  states: readonly (ResumableAdditionalTrialStateLike | null | undefined)[],
): LanguageTrialType[] => {
  const languages = new Set<LanguageTrialType>()

  for (const state of states) {
    if (
      state?.entrySource === 'legacy_trial' &&
      state.selectedLevel &&
      !state.trialBooked &&
      isLanguageTrialType(state.language)
    ) {
      languages.add(state.language)
    }
  }

  return LANGUAGE_TRIAL_TYPES.filter((language) => languages.has(language))
}

export const shouldShowLanguageTrialBanner = ({
  langType,
  paymentInfo,
  trialPaymentInfo,
  purchaseableTrialLanguages,
  bookedOnboardingLanguages,
  resumableOnboardingLanguages = [],
}: {
  langType: LanguageTrialType
  paymentInfo: string | null
  trialPaymentInfo: string | null
  purchaseableTrialLanguages: readonly LanguageTrialType[]
  bookedOnboardingLanguages: readonly LanguageTrialType[]
  resumableOnboardingLanguages?: readonly LanguageTrialType[]
}): boolean => {
  if (!purchaseableTrialLanguages.includes(langType) && !resumableOnboardingLanguages.includes(langType)) {
    return false
  }

  const paymentLanguages = parseLanguageInfo(paymentInfo)
  const trialPaymentLanguages = parseLanguageInfo(trialPaymentInfo)
  const hasAnyPreviousLanguage = paymentLanguages.size > 0 || trialPaymentLanguages.size > 0

  if (!hasAnyPreviousLanguage) {
    return false
  }

  if (
    paymentLanguages.has(langType) ||
    paymentLanguages.has(BUNDLE_LANGUAGE_TYPE) ||
    trialPaymentLanguages.has(langType) ||
    trialPaymentLanguages.has(BUNDLE_LANGUAGE_TYPE)
  ) {
    return false
  }

  return !bookedOnboardingLanguages.includes(langType)
}

export const getSkippedAdditionalTrialOnboardingState = ({
  states,
  paymentInfo,
  trialPaymentInfo,
  purchaseableTrialLanguages,
  bookedOnboardingLanguages,
}: {
  states: readonly (OnboardingHomeState | null | undefined)[]
  paymentInfo: string | null
  trialPaymentInfo: string | null
  purchaseableTrialLanguages: readonly LanguageTrialType[]
  bookedOnboardingLanguages: readonly LanguageTrialType[]
}) => {
  return (
    states.find((state) => {
      const langType = normalizeTrialLanguage(state?.language ?? null)

      return Boolean(
        state?.entrySource === 'legacy_trial' &&
        state.completed &&
        state.trialSkipped &&
        !state.trialBooked &&
        !state.selectedClassId &&
        langType &&
        shouldShowLanguageTrialBanner({
          langType,
          paymentInfo,
          trialPaymentInfo,
          purchaseableTrialLanguages,
          bookedOnboardingLanguages,
          resumableOnboardingLanguages: [langType],
        }),
      )
    }) ?? null
  )
}

export const getHasSkippedAdditionalTrialOnboardingDraft = ({
  draft,
  paymentInfo,
  trialPaymentInfo,
  purchaseableTrialLanguages,
  bookedOnboardingLanguages,
}: {
  draft: OnboardingDraft | null | undefined
  paymentInfo: string | null
  trialPaymentInfo: string | null
  purchaseableTrialLanguages: readonly LanguageTrialType[]
  bookedOnboardingLanguages: readonly LanguageTrialType[]
}) => {
  const langType = normalizeTrialLanguage(draft?.language ?? null)

  return Boolean(
    draft?.entrySource === 'legacy_trial' &&
    draft.status === 'completed' &&
    draft.trialSkipped &&
    !draft.trialBooked &&
    !draft.selectedClassId &&
    langType &&
    shouldShowLanguageTrialBanner({
      langType,
      paymentInfo,
      trialPaymentInfo,
      purchaseableTrialLanguages,
      bookedOnboardingLanguages,
      resumableOnboardingLanguages: [langType],
    }),
  )
}

export const createOnboardingDraftFromState = (
  state: OnboardingHomeState,
  ownerUserId?: OnboardingOwnerUserId,
): OnboardingDraft => {
  return withOnboardingDraftOwner(
    {
      ...DEFAULT_ONBOARDING_DRAFT,
      status: state.completed || state.trialBooked ? 'completed' : 'in-progress',
      entrySource: normalizeEntrySource(state.entrySource),
      language: normalizeLanguage(state.language),
      bookingLanguage: normalizeBookingLanguage(state.language),
      goals: state.goals as OnboardingGoal[],
      experienceLevel: state.experienceLevel as OnboardingDraft['experienceLevel'],
      frequency: state.frequency,
      preferredTime: state.preferredTimes as OnboardingPreferredTime[],
      classStyle: state.classStyles as OnboardingClassStyle[],
      selectedLevel: state.selectedLevel,
      recommendedLevel: state.recommendedLevel,
      resultStyleTags: state.styleTags,
      resultLessonStyleTags: state.lessonStyleTags,
      selectedClassId: state.selectedClassId,
      prestudyClassId: state.prestudyClassId ?? null,
      selectedClassDatetime: state.selectedClassDatetime ?? null,
      trialBooked: state.trialBooked,
      trialSkipped: state.trialSkipped,
      marketingAgreed: state.marketingAgreed,
      notificationResponded: state.notificationResponded,
    },
    ownerUserId,
  )
}

export const shouldShowOnboardingBookedCard = ({
  draft,
  userPaymentYn,
  userTrialPaymentYn,
  userTrialClassCompYn,
  hasUpcomingLesson,
  upcomingClassId,
  hasBookedOnboardingState = false,
}: HomeBookedCardParams) => {
  const hasUpcomingClassFilter = upcomingClassId !== undefined
  const hasDraftBooking = Boolean(
    draft.trialBooked &&
    draft.selectedClassId &&
    (!hasUpcomingClassFilter || Number(draft.selectedClassId) === upcomingClassId),
  )
  const hasTrialPaymentBooking = userPaymentYn === 'N' && userTrialPaymentYn === 'Y' && userTrialClassCompYn === 'N'

  return Boolean((hasDraftBooking || hasTrialPaymentBooking || hasBookedOnboardingState) && hasUpcomingLesson)
}

export const shouldShowOnboardingTrialCard = (params: HomeBookedCardParams) => {
  if (shouldShowOnboardingBookedCard(params)) {
    return false
  }

  if (params.hasRegularLessonEntitlement) {
    return false
  }

  if (params.hasSkippedAdditionalTrialOnboardingState) {
    return params.userPaymentYn === 'N'
  }

  return params.userPaymentYn === 'N' && params.userTrialPaymentYn === 'N' && params.userTrialClassCompYn === 'N'
}
